import { collapseToShellCommand } from 'src/components/publishSchema/continuousIntegrationTab/ContinuousIntegrationTab';

import Config from './config';
import { escapeShellArgument } from './escapeShellArgument';
import { notProduction } from './notProduction';

const getGraphRef = (graphId: string, graphVariant: string) =>
  `${escapeShellArgument(graphId)}@${escapeShellArgument(graphVariant)}`;

export const GRAPH_VARIANT_PLACEHOLDER = 'your-new-variant';
export const introspectionToGraphPublish = ({
  apiKey,
  graphId,
  graphVariant,
  highlightGraphRef,
}: {
  apiKey?: string;
  graphId: string;
  graphVariant: string;
  highlightGraphRef: boolean;
}) => ({
  shellString: collapseToShellCommand(
    'rover graph introspect',
    'http://localhost:4001 |',
    notProduction && `APOLLO_REGISTRY_URL=${Config.platformApiStagingEndpoint}`,
    !!apiKey && `APOLLO_KEY=${apiKey}`,
    `rover graph publish ${getGraphRef(graphId, graphVariant)}`,
    '--schema -',
  ),
  shellStringLinesToHighlight: [
    2,
    ...[...(highlightGraphRef ? [3] : []), 4, 5, 6].map(
      (line) => line + (apiKey ? 1 : 0) + (notProduction ? 1 : 0),
    ),
  ],
});

export const schemaFileToGraphPublish = ({
  apiKey,
  graphId,
  graphVariant,
  schemaLocation = '"path-to-your-subgraph-schema.{graphql,gql}"',
  highlightGraphRef,
}: {
  apiKey?: string;
  graphId: string;
  graphVariant: string;
  schemaLocation?: string;
  highlightGraphRef: boolean;
}) => ({
  shellString: collapseToShellCommand(
    notProduction && `APOLLO_REGISTRY_URL=${Config.platformApiStagingEndpoint}`,
    !!apiKey && `APOLLO_KEY=${apiKey}`,
    `rover graph publish ${getGraphRef(graphId, graphVariant)}`,
    `--schema ${schemaLocation}`,
  ),
  shellStringLinesToHighlight: [...(highlightGraphRef ? [1] : []), 2, 3, 4].map(
    (line) => line + (apiKey ? 1 : 0) + (notProduction ? 1 : 0),
  ),
});

export const downloadSubgraph = ({
  apiKey,
  graphRef,
  subgraphName,
}: {
  apiKey?: string;
  graphRef: string;
  subgraphName: string;
}) => ({
  shellString: collapseToShellCommand(
    notProduction && `APOLLO_REGISTRY_URL=${Config.platformApiStagingEndpoint}`,
    !!apiKey && `APOLLO_KEY=${apiKey}`,
    `rover subgraph fetch ${graphRef}`,
    `--name ${subgraphName} > ${subgraphName}.graphql`,
  ),
});

export const runRoverDev = ({
  apiKey,
  graphRef,
  routerVersion,
}: {
  apiKey?: string;
  graphRef: string;
  routerVersion: string;
}) => ({
  shellString: collapseToShellCommand(
    notProduction && `APOLLO_REGISTRY_URL=${Config.platformApiStagingEndpoint}`,
    notProduction && `APOLLO_UPLINK_ENDPOINTS=${Config.uplinkStagingEndpoint}`,
    !!apiKey && `APOLLO_KEY=${apiKey}`,
    `APOLLO_GRAPH_REF=${graphRef}`,
    `APOLLO_ROVER_DEV_ROUTER_VERSION=${routerVersion}`,
    `rover dev --supergraph-config supergraph.yaml`,
  ),
});

export const introspectionToSubgraphPublish = ({
  apiKey,
  graphId,
  graphVariant,
  routingUrl = '"your-subgraph-public-endpoint"',
  highlightGraphRef,
}: {
  apiKey?: string;
  graphId: string;
  graphVariant: string;
  routingUrl?: string;
  highlightGraphRef: boolean;
}) => ({
  shellString: collapseToShellCommand(
    'rover subgraph introspect',
    'http://localhost:4001 |',
    notProduction && `APOLLO_REGISTRY_URL=${Config.platformApiStagingEndpoint}`,
    !!apiKey && `APOLLO_KEY=${apiKey}`,
    `rover subgraph publish ${getGraphRef(graphId, graphVariant)}`,
    '--name your-subgraph-name',
    '--schema -',
    `--routing-url ${routingUrl}`,
  ),
  shellStringLinesToHighlight: [
    2,
    ...[...(highlightGraphRef ? [3] : []), 4, 5, 6].map(
      (line) => line + (apiKey ? 1 : 0) + (notProduction ? 1 : 0),
    ),
  ],
});

export const schemaFileToSubgraphPublish = ({
  apiKey,
  graphId,
  graphVariant,
  schemaLocation = '"path-to-your-subgraph-schema.{graphql,gql}"',
  routingUrl = '"your-subgraph-public-endpoint"',
  highlightGraphRef,
}: {
  apiKey?: string;
  graphId: string;
  graphVariant: string;
  schemaLocation?: string;
  routingUrl?: string;
  highlightGraphRef: boolean;
}) => ({
  shellString: collapseToShellCommand(
    notProduction && `APOLLO_REGISTRY_URL=${Config.platformApiStagingEndpoint}`,
    !!apiKey && `APOLLO_KEY=${apiKey}`,
    `rover subgraph publish ${getGraphRef(graphId, graphVariant)}`,
    `--schema ${schemaLocation}`,
    '--name your-subgraph-name',
    `--routing-url ${routingUrl}`,
  ),
  shellStringLinesToHighlight: [...(highlightGraphRef ? [1] : []), 2, 3, 4].map(
    (line) => line + (apiKey ? 1 : 0) + (notProduction ? 1 : 0),
  ),
});

export const schemaFileToSubgraphCheck = ({
  apiKey,
  graphId,
  graphVariant,
  schemaLocation = '"path-to-your-subgraph-schema.{graphql,gql}"',
  highlightGraphRef,
}: {
  apiKey?: string;
  graphId: string;
  graphVariant: string;
  schemaLocation?: string;
  highlightGraphRef: boolean;
}) => ({
  shellString: collapseToShellCommand(
    notProduction && `APOLLO_REGISTRY_URL=${Config.platformApiStagingEndpoint}`,
    !!apiKey && `APOLLO_KEY=${apiKey}`,
    `rover subgraph check ${getGraphRef(graphId, graphVariant)}`,
    `--schema ${schemaLocation}`,
    '--name your-subgraph-name',
  ),
  shellStringLinesToHighlight: [...(highlightGraphRef ? [1] : []), 2, 3].map(
    (line) => line + (apiKey ? 1 : 0) + (notProduction ? 1 : 0),
  ),
});

export const introspectionToSubgraphCheck = ({
  apiKey,
  graphId,
  graphVariant,
  highlightGraphRef,
}: {
  apiKey?: string;
  graphId: string;
  graphVariant: string;
  highlightGraphRef: boolean;
}) => ({
  shellString: collapseToShellCommand(
    'rover subgraph introspect',
    'http://localhost:4001 |',
    notProduction && `APOLLO_REGISTRY_URL=${Config.platformApiStagingEndpoint}`,
    !!apiKey && `APOLLO_KEY=${apiKey}`,
    `rover subgraph check ${getGraphRef(graphId, graphVariant)}`,
    `--schema -`,
    '--name your-subgraph-name',
  ),
  shellStringLinesToHighlight: [
    2,
    ...[...(highlightGraphRef ? [3] : []), 4, 5].map(
      (line) => line + (apiKey ? 1 : 0) + (notProduction ? 1 : 0),
    ),
  ],
});
